import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Components
import { SelectedCisContext, useSelectedCisContext } from "../context/SelectedCisContext";
import CiDataGrid from "./CiDataGrid";
import CiFiltersPanel from "./CiFiltersPanel";
import CisToolbar from "./CiToolbar";
import ItemDeleteDialog from "../../../shared/components/ItemDeleteDialog";

// Business logic
import { useDeleteCi } from "../hooks/useCiMutations";
import { parseQueryParams } from "../../../shared/services/queryParamsService";

const CI_QUERY_SCHEMA = {
  name: "string",
  type_id: "number",
  status_id: "number",
  owner_id: "number",
  site_id: "number",
};

function CisContent() {
  const [isRemoverOpen, setIsRemoverOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { value: selectedCis } = useSelectedCisContext();

  // Parameters
  const location = useLocation();
  const navigate = useNavigate();
  const [queryParameters, setQueryParameters] = useState(parseQueryParams(location.search, CI_QUERY_SCHEMA));
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load saved query parameters from localStorage on mount
  useEffect(() => {
    if (isInitialLoad && (!location.search || location.search === "")) {
      try {
        const savedParams = localStorage.getItem("ci_query_params");
        if (savedParams) {
          const parsedParams = JSON.parse(savedParams);
          const params = new URLSearchParams();
          Object.entries(parsedParams).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== "") {
              params.append(key, value);
            }
          });
          if (params.toString()) {
            navigate({ search: params.toString() }, { replace: true });
          }
        }
      } catch (error) {
        console.error("Failed to load saved query parameters:", error);
      }
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, location.search, navigate]);

  // useEffects
  useEffect(() => {
    const newQueryParams = parseQueryParams(location.search, CI_QUERY_SCHEMA);
    setQueryParameters(newQueryParams);

    // Save to localStorage whenever query parameters change
    const hasActiveParams = Object.values(newQueryParams).some((v) => v !== null && v !== undefined && v !== "");
    if (hasActiveParams) {
      localStorage.setItem("ci_query_params", JSON.stringify(newQueryParams));
    } else {
      localStorage.removeItem("ci_query_params");
    }
  }, [location.search]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <CisToolbar
        setIsRemoverOpen={setIsRemoverOpen}
        setIsFiltersOpen={setIsFiltersOpen}
        queryParameters={queryParameters}
      />
      <div style={{ position: "relative", display: "flex", flex: 1, overflow: "hidden", width: "100%" }}>
        <CiDataGrid data-testid="cis-datagrid" />
        <ItemDeleteDialog
          isOpen={isRemoverOpen}
          setIsOpen={setIsRemoverOpen}
          useMutationHook={useDeleteCi}
          queryKey={["cis"]}
          id={selectedCis?.[0]?.id}
          name={selectedCis?.[0]?.name}
        />
        <CiFiltersPanel
          isOpen={isFiltersOpen}
          setIsOpen={setIsFiltersOpen}
          queryParameters={queryParameters}
          setQueryParameters={setQueryParameters}
        />
      </div>
    </div>
  );
}

function Cis() {
  return (
    <SelectedCisContext>
      <CisContent />
    </SelectedCisContext>
  );
}

export default Cis;
